import { createContext, useContext } from 'react';
import { cn } from '@/lib/utils';
import type { McGrawHillReferenceRole } from './mcgrawHillReferences';

/**
 * What an answer's badges need from it: how to open a numbered source, and
 * each number's colour (client documents red, articles green).
 */
export interface McGrawHillCiteContextValue {
  open?: (number: number) => void;
  roles: ReadonlyMap<number, McGrawHillReferenceRole>;
}

/** Supplied by the answer that numbers its sources. */
export const McGrawHillCiteContext =
  createContext<McGrawHillCiteContextValue | null>(null);

/**
 * mcgraw-hill: a numbered source badge inside an answer (`[[cite N]]`). It
 * opens its source's page when the answer supplies a way to.
 */
const McGrawHillCiteBadge = ({ number }: { number: number }) => {
  const context = useContext(McGrawHillCiteContext);

  // Outside a McGraw Hill answer the token is just text, as it always was.
  if (!context) return <>{`[[cite ${number}]]`}</>;

  const open = context.open;

  const className = cn(
    'mcgraw-hill-cite',
    context.roles.get(number) === 'article' && 'mcgraw-hill-cite--article'
  );

  return open ? (
    <button
      type="button"
      className={className}
      onClick={() => open(number)}
      aria-label={`Open source ${number}`}
    >
      {number}
    </button>
  ) : (
    <span className={className}>{number}</span>
  );
};

export default McGrawHillCiteBadge;
